import React, { useState, useEffect } from 'react';
import SectionHeading from '../SectionHeading';
import Spacing from '../Spacing';
import { Star, CheckCircle, StarHalf } from 'lucide-react';
import AnimatedSection from '../AnimatedSection';
import { reviewAPI } from '../../services/api';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../ui/carousel";
import Autoplay from "embla-carousel-autoplay";

const fallbackReviews = [
  {
    name: "Rajesh S.",
    location: "Mumbai",
    text: "It has become a reliable part of my daily wellness routine. I feel more active and balanced. Highly recommended!",
    rating: 5,
    verified: true
  },
  {
    name: "Amit P.",
    location: "Delhi",
    text: "Made with natural ingredients. I feel more balanced and active throughout the day. It definitely supports my routine.",
    rating: 5,
    verified: true
  },
  {
    name: "Vikram R.",
    location: "Bangalore",
    text: "I've tried other brands but nothing worked like this. It took a few weeks to feel fully balanced, but it was absolutely worth it.",
    rating: 4,
    verified: true
  }
];

export default function Reviews() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await reviewAPI.getReviews({ limit: 10 });
        
        console.log("API Fetch Reviews Response:", response.data);
        
        // Handle various API response formats commonly used
        const success = response.data?.status === 200 || response.data?.success || response.status === 200;
        const apiReviews = response.data?.data || response.data?.reviews || (Array.isArray(response.data) ? response.data : null);
        
        if (success && apiReviews && apiReviews.length > 0) {
          setReviews(apiReviews);
        } else {
          setReviews(fallbackReviews);
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setReviews(fallbackReviews);
      }
    };
    fetchReviews();
  }, []);

  return (
    <section id="reviews" className="cs_gray_bg">
      <Spacing lg="120" md="80" />
      <div className="container">
        <div className="text-center">
          <AnimatedSection animation="fadeUp">
            <SectionHeading 
              title="Real Stories, Real Results" 
              subTitle="Customer Reviews" 
            />
          </AnimatedSection>
        </div>
        <Spacing lg="40" md="30" />
        
        {/* Overall Review Stats Summary */}
        <AnimatedSection animation="fadeUp" delay={0.1}>
          <div className="d-flex flex-column align-items-center justify-content-center mb-5 pb-4" style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
            <div className="d-flex align-items-center mb-2">
              <span className="cs_bold me-3" style={{ fontSize: 'clamp(40px, 6vw, 56px)', lineHeight: 1, letterSpacing: '-1px' }}>4.8</span>
              <div className="d-flex flex-column">
                <div className="d-flex" style={{ color: '#FFD700' }}>
                  <Star fill="currentColor" size={24} />
                  <Star fill="currentColor" size={24} />
                  <Star fill="currentColor" size={24} />
                  <Star fill="currentColor" size={24} />
                  <StarHalf fill="currentColor" size={24} />
                </div>
                {/* <span className="text-muted mt-1 fw-medium" style={{ fontSize: 'clamp(13px, 2vw, 15px)' }}>Based on 10,000+ Reviews</span> */}
              </div>
            </div>
            
            <div className="d-flex align-items-center mt-3 flex-wrap justify-content-center gap-3">
              <div className="d-flex align-items-center text-dark">
                <CheckCircle size={18} color="#2ECC71" className="me-2" />
                <span className="cs_semibold" style={{ fontSize: 'clamp(14px, 2vw, 16px)' }}>Verified Buyers Only</span>
              </div>
              <div className="d-flex align-items-center text-dark">
                <CheckCircle size={18} color="#2ECC71" className="me-2 text-md-start border-md-start ps-md-3" />
                <span className="cs_semibold" style={{ fontSize: 'clamp(14px, 2vw, 16px)' }}>98% Recommendation Rate</span>
              </div>
            </div>
          </div>
        </AnimatedSection>
        
        <div className="cs_slider_wrap px-4 pb-5 position-relative w-100 mx-auto" style={{ maxWidth: '1140px' }}>
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            plugins={[
              Autoplay({
                delay: 3000,
              }),
            ]}
            className="w-100"
          >
            <CarouselContent className="d-flex" style={{ marginLeft: '-1rem' }}>
              {reviews.map((review, index) => (
                <CarouselItem key={index} className="col-12 col-md-6 col-lg-4 ps-3 pe-0 pb-4 flex-shrink-0" style={{ minWidth: 0 }}>
                  <AnimatedSection animation="fadeUp" delay={index * 0.15} style={{ height: '100%' }}>
                    <div className="cs_review_card cs_radius_15 cs_white_bg p-4 h-full flex flex-col transition-transform duration-300 hover:-translate-y-1" style={{
                      boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                      minHeight: '260px',
                      borderTop: '4px solid #0B5E4B',
                    }}>
                      <div className="d-flex mb-3">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            size={20} 
                            fill={i < (review.rating || 5) ? "#FFD700" : "none"} 
                            color={i < (review.rating || 5) ? "#FFD700" : "#ccc"} 
                            className="me-1"
                          />
                        ))}
                      </div>
                      <p className="cs_fs_16 mb-4 flex-grow-1" style={{ fontStyle: 'italic', color: '#555' }}>
                        "{review.review_text || review.text || review.comment}"
                      </p>
                      <div className="d-flex align-items-center justify-content-between mt-auto pt-3" style={{ borderTop: '1px solid #eee' }}>
                        <div>
                          <h4 className="cs_fs_18 cs_semibold mb-1">{review.reviewer_name || review.customer_name || review.name || 'Verified Customer'}</h4>
                          <p className="cs_fs_14 mb-0 text-muted">{review.location || 'India'}</p>
                        </div>
                        {(review.verified !== false) && (
                          <div className="d-flex align-items-center" style={{ color: '#2ECC71' }}>
                            <CheckCircle size={16} className="me-1" />
                            <span className="cs_fs_12 fw-bold">Verified</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </AnimatedSection>
                </CarouselItem>
              ))}
            </CarouselContent>
            {/* <CarouselPrevious className="position-absolute d-none d-md-flex align-items-center justify-content-center border-0 bg-white rounded-circle shadow" style={{ left: '-2rem', top: '50%', transform: 'translateY(-50%)', height: '3rem', width: '3rem', zIndex: 10, color: '#0B5E4B' }} />
            <CarouselNext className="position-absolute d-none d-md-flex align-items-center justify-content-center border-0 bg-white rounded-circle shadow" style={{ right: '-2rem', top: '50%', transform: 'translateY(-50%)', height: '3rem', width: '3rem', zIndex: 10, color: '#0B5E4B' }} /> */}
          </Carousel>
        </div>
      </div>
      <Spacing lg="120" md="80" />
    </section>
  );
}
